import {css, StyleSheet} from 'aphrodite';
import reactUpdate from 'immutability-helper';
import React from 'react';

import ModelTypeConstants from 'constants/ModelTypeConstants';
import StoichiometryTableEmbeddedChemicalSketcher from 'notebook/integrations/chemistry/reaction-scheme/StoichiometryTableEmbeddedChemicalSketcher';
import {EMPTY_SEARCH_RESULT_PROPS} from 'notebook/mediocre/search/SearchResultProps';
import SelectableNotePart from 'notebook/mediocre/selection/block/SelectableNotePart';
import {makeDefaultItem} from 'notebook/notes/CreateOps';
import GroupTitleBar from 'notebook/notes/groups/GroupTitleBar';
import {getNoteLinkedGroupAdapters} from 'notebook/notes/groups/NoteLinkedGroupAdapters';
import {
  GroupActionFunctionParameters,
  NoteLinkedGroupKind,
  StructuredTableSpreadsheetProps,
} from 'notebook/notes/groups/NoteLinkedGroupConstants';
import {reduceGroupUpdateNotePartItem} from 'notebook/notes/groups/util/ContainedNotePartUtil';
import groupKindToIcon from 'notebook/notes/groups/util/groupKindToIcon';
import maybeAsyncUpdateContainedNotePart from 'notebook/notes/groups/util/maybeAsyncUpdateContainedNotePart';
import {makeTableItemPropsByName} from 'notebook/notes/groups/util/NoteLinkedGroupViewSpreadsheetProps';
import {NoteEditor, NoteEditorProps} from 'notebook/notes/NoteEditorTypes';
import {
  ActionFunctionContext,
  useActionFunctionContext,
} from 'notebook/structured-tables/action-functions/ActionFunctionContext';
import {getStructuredTableAdapters} from 'notebook/structured-tables/StructuredTableAdapters';
import StructuredTableFormOrView from 'notebook/structured-tables/StructuredTableFormOrView';
import {NoteLinkedStructuredTable} from 'notebook/structured-tables/StructuredTablePlugin';
import {type TableOpDiffMap} from 'notebook/tables/TableConstants';
import {NoteLinkedFile, NotePart} from 'notebook/util/NoteUtilConstants';
import {GRAY} from 'ui-library/Colors';
import OverflowMenu from 'ui-library/menus/OverflowMenu';
import memoizeLastCall from 'util/memoizeLastCall';

import {
  makeEmptyReactionSchemeFile,
  StoichiometryCreateGroupExtras,
  StoichiometryGroup,
  StoichiometryGroupMetadata,
  StoichiometryNoteLinkedGroupAdapter,
} from './StoichiometryNoteLinkedGroupAdapter';
import {StoichiometryStructuredTable} from './StoichiometryStructuredTableAdapter';

export interface StoichiometryGroupViewProps {
  notePartCid: string;
  group: StoichiometryGroup;
  editor: NoteEditor;
  editorProps: NoteEditorProps;
  spreadsheetProps: StructuredTableSpreadsheetProps;
}

interface ActionFunctionContextProps {
  actionFunctionContext: ActionFunctionContext;
}

function GroupActionButtons<TGroupMetadata extends {}, TCreateGroupExtras extends {}>(
  props: GroupActionFunctionParameters<TGroupMetadata, TCreateGroupExtras>
): JSX.Element {
  const actionFunctions = props.noteLinkedGroupAdapter.getActionFunctions();

  // When collapsed, only show the primary action function.
  return (
    <>
      {!props.isCollapsed &&
        actionFunctions.secondary?.map((actionFunction, index) => (
          <React.Fragment key={index}>{actionFunction.renderToolbarButton(props)}</React.Fragment>
        ))}
      {!props.isCollapsed && actionFunctions.overflow?.length ? (
        <OverflowMenu
          options={actionFunctions.overflow
            .map((actionFunction) => actionFunction.getOverflowMenuItem(props))
            .filter((option) => option != null)}
        />
      ) : null}
      {actionFunctions.primary?.renderToolbarButton(props)}
    </>
  );
}

interface StoichiometryGroupInnerViewState {
  isCollapsed: boolean;
}

class StoichiometryGroupInnerView extends React.Component<
  StoichiometryGroupViewProps & ActionFunctionContextProps,
  StoichiometryGroupInnerViewState
> {
  override state: StoichiometryGroupInnerViewState = {isCollapsed: false};

  private emptyReactionSchemeFile: NoteLinkedFile | null = null;
  private emptyReactionSchemeFileName: string | null = null;

  private getOrCreateEmptyReactionSchemeFile(name: string): NoteLinkedFile {
    if (this.emptyReactionSchemeFile == null || this.emptyReactionSchemeFileName !== name) {
      this.emptyReactionSchemeFile = makeEmptyReactionSchemeFile(name);
      this.emptyReactionSchemeFileName = name;
    }
    return this.emptyReactionSchemeFile;
  }

  handleUpdate = (updatedFile: NoteLinkedFile, updatedTable: StoichiometryStructuredTable): void => {
    const {group, editor, editorProps, notePartCid} = this.props;
    const adapter = getNoteLinkedGroupAdapters()[
      NoteLinkedGroupKind.STOICHIOMETRY_GROUP
    ] as StoichiometryNoteLinkedGroupAdapter;
    const reactionSchemePart = adapter.getReactionSchemeNotePart(group);
    const tablePart = adapter.getStoichiometryTableNotePart(group);
    // The NoteLinkedFile's cid is the server-assigned uuid from process_reaction_components
    // (set in handleReactionSketcherSubmit). Register the cid → id mapping so the patcher
    // can find the existing NoteLinkedFile by id on save.
    if (updatedFile.cid != null) {
      editorProps.registerObjects({[updatedFile.cid]: updatedFile.cid});
    }
    let updatedGroup: StoichiometryGroup = group;
    if (reactionSchemePart != null) {
      updatedGroup = reduceGroupUpdateNotePartItem(updatedGroup, reactionSchemePart, updatedFile);
    } else {
      // No NoteLinkedFile part exists yet — add one to the group.
      const newReactionSchemePart = reactUpdate(makeDefaultItem(ModelTypeConstants.NOTE_LINKED_OBJECT), {
        item: {$set: updatedFile},
      });
      updatedGroup = reactUpdate(updatedGroup, {
        containedNoteParts: {$unshift: [newReactionSchemePart]},
      });
    }
    updatedGroup = reduceGroupUpdateNotePartItem(updatedGroup, tablePart, updatedTable);
    editor.itemUpdateHandler(editor.getIndex(notePartCid), {item: updatedGroup});
  };

  // Memoized stable table item props, following the entry-level evaluation pattern
  // from NoteLinkedGroupEntryLevelView. Extra dependencies ensure memoization detects
  // changes in editor.props values that don't change the editor reference itself.
  _getStableTableItemProps = memoizeLastCall(
    (
      group: StoichiometryGroup,
      editor: NoteEditor,
      spreadsheetProps: StructuredTableSpreadsheetProps,
      _cellColors: unknown,
      _editingTable: unknown,
      _computedTables: unknown
    ) => makeTableItemPropsByName(group, editor, spreadsheetProps)
  );

  override render(): JSX.Element {
    const {group, editor, editorProps, notePartCid, spreadsheetProps, actionFunctionContext} = this.props;
    const {isCollapsed} = this.state;
    const adapter = getNoteLinkedGroupAdapters()[
      NoteLinkedGroupKind.STOICHIOMETRY_GROUP
    ] as StoichiometryNoteLinkedGroupAdapter;
    const {evaluatedTableMap, tableItemPropsByName} = this._getStableTableItemProps(
      group,
      editor,
      spreadsheetProps,
      editor.props.cellColors,
      editor.props.editingTable,
      editor.props.computedTables
    );

    const reactionSchemePart = adapter.getReactionSchemeNotePart(group);
    const tablePart = adapter.getStoichiometryTableNotePart(group);
    const tablePartIndex = group.containedNoteParts.indexOf(tablePart);
    const structuredTable = tablePart.item;
    const tableAdapter = getStructuredTableAdapters()[structuredTable.kind];
    const tableData = tableAdapter.toTable(structuredTable);
    const computedTable = evaluatedTableMap[tableData.name];
    const tableItemProps = tableItemPropsByName[tableData.name];

    const actionFunctionParameters: GroupActionFunctionParameters<
      StoichiometryGroupMetadata,
      StoichiometryCreateGroupExtras
    > = {
      entryOrTemplateId: editorProps.fileId,
      noteLinkedGroup: group,
      noteLinkedGroupAdapter: adapter,
      locked: editorProps.locked,
      isCollapsed,
      evaluatedTableMap,
      actionFunctionContext,
      getItemWithUpdatedContent: editor.getItemWithUpdatedContent,
      getIdFromCidFn: editorProps.getIdFromCid,
      updateGroup: (noteLinkedGroup) => {
        editor.itemUpdateHandler(editor.getIndex(notePartCid), {item: noteLinkedGroup});
      },
      insertAfterGroup: (noteParts, cidIdMap) => {
        editor.props.registerObjects(cidIdMap);
        editor.itemInsertHandler(editor.getIndex(notePartCid), ...noteParts);
      },
      selectorsById: {},
    };

    const isExpanded = !isCollapsed || editorProps.viewer;
    const printableContainerClass = editorProps.viewer ? 'printable-container-scaleForPrint' : '';

    return (
      <SelectableNotePart indentation={0} className={printableContainerClass}>
        <div className={css(styles.groupWrapper)}>
          <GroupTitleBar
            groupTypeLabel={adapter.getDisplayName(group)}
            groupName={group.name}
            icon={groupKindToIcon(group.kind)}
            rightHeaderControls={<GroupActionButtons {...actionFunctionParameters} />}
            isCollapsed={isCollapsed}
            onToggleCollapsed={() => {
              this.setState({isCollapsed: !isCollapsed});
            }}
            viewer={editorProps.viewer}
          />
          {isExpanded && (
            <div
              role="presentation"
              onMouseDown={(e) => {
                e.stopPropagation();
              }}
              onMouseOver={(e) => {
                e.stopPropagation();
              }}
            >
              <StoichiometryTableEmbeddedChemicalSketcher
                reactionSchemeFile={
                  reactionSchemePart?.item ?? this.getOrCreateEmptyReactionSchemeFile(group.name)
                }
                onUpdate={this.handleUpdate}
                structuredTable={structuredTable}
                computedTable={computedTable ?? []}
                viewer={editorProps.viewer}
              />
              {tableItemProps != null && (
                <StructuredTableFormOrView
                  editor={editor}
                  editorProps={editorProps}
                  index={tablePartIndex}
                  notePart={tablePart as unknown as NotePart & {item: NoteLinkedStructuredTable}}
                  onUpdateContent={async (
                    containedNotePartIndex: number,
                    content: object,
                    _selectors?: never,
                    tableOp?: TableOpDiffMap
                  ): Promise<void> => {
                    const oldContainedNotePart = group.containedNoteParts[containedNotePartIndex];
                    const updatedContainedNotePart = editor.getItemWithUpdatedContent(
                      oldContainedNotePart,
                      content
                    );
                    await maybeAsyncUpdateContainedNotePart(
                      group,
                      editor,
                      notePartCid,
                      updatedContainedNotePart,
                      tableOp
                    );
                  }}
                  isInGroup={true}
                  itemProps={tableItemProps}
                  adapter={tableAdapter}
                  overrideDefaultTableProps={{
                    disableMaximizedTables: true,
                    isCollapsible: false,
                  }}
                  {...EMPTY_SEARCH_RESULT_PROPS}
                />
              )}
            </div>
          )}
        </div>
      </SelectableNotePart>
    );
  }
}

export function StoichiometryGroupView(props: StoichiometryGroupViewProps): JSX.Element {
  const actionFunctionContext = useActionFunctionContext();
  return <StoichiometryGroupInnerView {...props} actionFunctionContext={actionFunctionContext} />;
}

const styles = StyleSheet.create({
  groupWrapper: {
    backgroundColor: 'white',
    border: `1px solid ${GRAY}`,
    borderRadius: '4px',
    display: 'inline-block',
    maxWidth: '100%',
    position: 'relative',
    width: '100%',
  },
});
